/**
 * Tab Container Component
 * @module components/layout/tabs/TabContainer
 */

import GlobalErrorBoundary from "@components/common/errors/GlobalErrorBoundary";
import type { Tab } from "@lib/tabs/types";
import { MobileTabs } from "./MobileTabs";
import { TabLink } from "./TabLink";
import { TabSkeleton } from "./TabSkeleton";

/**
 * Tab container props
 */
export interface TabContainerProps {
  /** Tabs to display */
  tabs: Tab[];
  /** Loading state */
  isLoading?: boolean;
  /** Error state */
  error?: Error | null;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Unified tab container — same design everywhere (dashboard + module).
 */
export function TabContainer({
  tabs,
  isLoading = false,
  error = null,
  className = "",
}: TabContainerProps) {
  if (error) {
    return (
      <div className={`tab-container ${className}`}>
        <div className="px-4 py-3 text-error text-sm">Erreur de chargement des onglets</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`tab-container ${className}`}>
        <div className="flex items-center gap-3 py-2 px-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="tab-skeleton" />
          ))}
        </div>
      </div>
    );
  }

  if (!tabs.length) {
    return null;
  }

  return (
    <GlobalErrorBoundary componentName="TabContainer">
      <div className={`tab-container ${className}`}>
        {/* Desktop tabs */}
        <div
          role="tablist"
          className="hidden lg:flex items-center gap-0.5"
          aria-label="Navigation par onglets"
        >
          {tabs.map((tab) => (
            <TabLink key={tab.id} tab={tab} />
          ))}
        </div>

        {/* Mobile tabs */}
        <div className="lg:hidden">
          <MobileTabs tabs={tabs} />
        </div>
      </div>
    </GlobalErrorBoundary>
  );
}
